import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiEndpoint } from '../api-spec.model';
import { JsonSyntaxHighlightPipe } from '../pipes/json-syntax-highlight.pipe';

@Component({
  selector: 'app-endpoint',
  standalone: true,
  imports: [CommonModule, JsonSyntaxHighlightPipe],
  template: `
    <div class="border border-gray-200 rounded-lg overflow-hidden mb-6" [id]="endpoint.id" data-section>
      <!-- Header bar -->
      <div class="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 flex-wrap">
        <span class="px-2.5 py-1 text-xs font-bold rounded uppercase tracking-wide" [ngClass]="methodColor">
          {{ endpoint.method }}
        </span>
        <code class="text-sm font-mono text-gray-800 break-all">{{ endpoint.path }}</code>
        <span *ngIf="endpoint.scope"
          class="ml-auto px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded font-medium">
          {{ endpoint.scope }}
        </span>
      </div>

      <div class="p-5 space-y-5">
        <!-- Summary & Description -->
        <div>
          <h4 class="text-lg font-semibold text-gray-900 mb-1">{{ endpoint.summary }}</h4>
          <p class="text-sm text-gray-600 leading-relaxed">{{ endpoint.description }}</p>
        </div>

        <!-- Parameters Table -->
        <div *ngIf="endpoint.parameters?.length">
          <h5 class="text-sm font-semibold text-gray-700 mb-2">Parameters</h5>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Name</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">In</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Type</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Required</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Default</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let param of endpoint.parameters" class="border-t border-gray-100">
                  <td class="px-3 py-2 font-mono text-xs text-indigo-700 whitespace-nowrap">{{ param.name }}</td>
                  <td class="px-3 py-2 text-gray-500 text-xs">{{ param.location }}</td>
                  <td class="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{{ param.type }}</td>
                  <td class="px-3 py-2">
                    <span [class]="param.required ? 'text-red-600 font-medium text-xs' : 'text-gray-400 text-xs'">
                      {{ param.required ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 font-mono text-xs text-gray-500">{{ param.defaultValue || '—' }}</td>
                  <td class="px-3 py-2 text-gray-600 text-xs">{{ param.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Request Body -->
        <div *ngIf="endpoint.requestBody">
          <h5 class="text-sm font-semibold text-gray-700 mb-2">Request Body</h5>
          <div class="overflow-x-auto mb-3">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Field</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Required</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let field of endpoint.requestBody.fields" class="border-t border-gray-100">
                  <td class="px-3 py-2 font-mono text-xs text-indigo-700">{{ field.name }}</td>
                  <td class="px-3 py-2 text-gray-500 text-xs">{{ field.type }}</td>
                  <td class="px-3 py-2">
                    <span [class]="field.required ? 'text-red-600 font-medium text-xs' : 'text-gray-400 text-xs'">
                      {{ field.required ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-gray-600 text-xs">{{ field.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="relative">
            <span class="absolute top-2 right-3 text-[10px] text-gray-500 uppercase tracking-wider">Example</span>
            <pre class="code-block"><code [innerHTML]="endpoint.requestBody.example | jsonHighlight"></code></pre>
          </div>
        </div>

        <!-- Response Example -->
        <div>
          <h5 class="text-sm font-semibold text-gray-700 mb-2">
            Response
            <span class="text-emerald-600 font-normal ml-1">200 OK</span>
          </h5>
          <pre class="code-block"><code [innerHTML]="endpoint.responseExample | jsonHighlight"></code></pre>
        </div>

        <!-- Error Codes -->
        <div *ngIf="endpoint.errorCodes.length">
          <h5 class="text-sm font-semibold text-gray-700 mb-2">Error Responses</h5>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-3 py-2 text-left font-medium text-gray-600 w-24">Status</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let err of endpoint.errorCodes" class="border-t border-gray-100">
                  <td class="px-3 py-2">
                    <span class="px-2 py-0.5 text-xs font-bold rounded" [ngClass]="getStatusClass(err.status)">
                      {{ err.status }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-gray-600 text-xs">{{ err.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- cURL Example -->
        <div>
          <h5 class="text-sm font-semibold text-gray-700 mb-2">cURL</h5>
          <pre class="code-block text-xs whitespace-pre-wrap"><code>{{ endpoint.curlExample }}</code></pre>
        </div>

        <!-- Try It button -->
        <div class="border-t border-gray-200 pt-4">
          <button type="button" (click)="tryItClick.emit(endpoint)"
            class="flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
            Try It
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`:host
  display: block
.code-block
  background: #111827
  color: #e5e7eb
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Courier New', monospace
  font-size: 0.8125rem
  line-height: 1.6
  border-radius: 0.5rem
  padding: 1rem
  overflow-x: auto
  margin: 0
:host ::ng-deep .jh-key
  color: #c4b5fd
:host ::ng-deep .jh-string
  color: #6ee7b7
:host ::ng-deep .jh-number
  color: #93c5fd
:host ::ng-deep .jh-boolean
  color: #fcd34d
:host ::ng-deep .jh-null
  color: #9ca3af
`]
})
export class EndpointComponent {
  @Input() endpoint!: ApiEndpoint;
  @Output() tryItClick = new EventEmitter<ApiEndpoint>();

  get methodColor(): string {
    switch (this.endpoint.method) {
      case 'GET': return 'bg-emerald-100 text-emerald-700';
      case 'POST': return 'bg-blue-100 text-blue-700';
      case 'PUT': return 'bg-orange-100 text-orange-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  getStatusClass(status: number): string {
    if (status >= 200 && status < 300) return 'bg-emerald-100 text-emerald-700';
    if (status >= 400 && status < 500) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  }
}
